import { useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Mail, Send, RefreshCw, Eye, MousePointerClick, AlertTriangle,
  Bold, Italic, Heading1, Heading2, List, ListOrdered, Link as LinkIcon,
  Image as ImageIcon, MousePointer2, Save, FileText, Underline as UnderlineIcon, Trash2,
} from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";

const VARIABLES = [
  { key: "{{first_name}}", label: "Fornavn", example: "Peter" },
  { key: "{{name}}", label: "Fulde navn", example: "Peter Andersen" },
];

function ToolbarBtn({ onClick, active, disabled, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`h-8 w-8 flex items-center justify-center rounded-md text-sm border transition-colors ${
        active
          ? "bg-white text-black border-white"
          : "border-white/10 text-zinc-300 hover:bg-white/5"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;
  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (fx https:// eller /leaderboard)", prev);
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const promptButton = () => {
    const text = window.prompt("Knap-tekst", "SE DIN SCORE");
    if (!text) return;
    const url = window.prompt("Knap-link (fx https:// eller /leaderboard)", "/leaderboard");
    if (!url) return;
    const safeText = text.replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const safeUrl = url.replace(/"/g, "&quot;");
    editor.chain().focus().insertContent(
      `<p class="fm-btn-row"><a class="fm-btn" href="${safeUrl}">${safeText}</a></p><p></p>`
    ).run();
  };
  const promptImage = () => {
    const url = window.prompt("Billede-URL (offentligt tilgængeligt)", "");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };
  return (
    <div className="flex flex-wrap items-center gap-1 border border-white/10 rounded-t-md bg-black/40 px-2 py-2" data-testid="mail-toolbar">
      <ToolbarBtn testId="mail-tb-h1" title="Overskrift 1" active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-h2" title="Overskrift 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={14}/></ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn testId="mail-tb-bold" title="Fed" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-italic" title="Kursiv" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-underline" title="Understreg" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={14}/></ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn testId="mail-tb-ul" title="Punktliste" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-ol" title="Nummereret liste" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={14}/></ToolbarBtn>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolbarBtn testId="mail-tb-link" title="Indsæt link" active={editor.isActive("link")} onClick={promptLink}><LinkIcon size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-button" title="Indsæt knap" onClick={promptButton}><MousePointer2 size={14}/></ToolbarBtn>
      <ToolbarBtn testId="mail-tb-image" title="Indsæt billede" onClick={promptImage}><ImageIcon size={14}/></ToolbarBtn>
    </div>
  );
}

export default function MailCenter() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ["mail-status"], queryFn: () => api.get("/admin/mail/status").then(r => r.data) });
  const { data: audience } = useQuery({ queryKey: ["mail-audience"], queryFn: () => api.get("/admin/mail/audience").then(r => r.data) });
  const { data: campaigns } = useQuery({ queryKey: ["mail-campaigns"], queryFn: () => api.get("/admin/mail/campaigns").then(r => r.data) });
  const { data: templates } = useQuery({ queryKey: ["mail-templates"], queryFn: () => api.get("/admin/mail/templates").then(r => r.data) });

  const [subject, setSubject] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false, HTMLAttributes: { rel: "noopener" } },
      }),
      Image.configure({ HTMLAttributes: { style: "max-width:100%;height:auto;border-radius:8px;" } }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-invert min-h-[320px] max-w-none px-4 py-3 focus:outline-none",
        "data-testid": "mail-editor",
      },
    },
  });

  // Load default template on mount
  useEffect(() => {
    if (!editor || !templates) return;
    if (!subject && !editor.getText()) {
      const t = templates.find(x => x.template_id === "builtin_new_names");
      if (t) {
        setSubject(t.subject);
        editor.commands.setContent(t.content_html);
      }
    }
  }, [editor, templates]); // eslint-disable-line

  const updatePreview = useCallback(async () => {
    if (!editor) return;
    try {
      const r = await api.post("/admin/mail/render", { subject, content_html: editor.getHTML() });
      setPreview(r.data);
    } catch (e) { /* silent */ }
  }, [editor, subject]);

  // Auto-refresh preview when editor content or subject changes
  useEffect(() => {
    if (!editor) return;
    const t = setTimeout(updatePreview, 350);
    return () => clearTimeout(t);
  }, [editor, subject, updatePreview]);

  useEffect(() => {
    if (!editor) return;
    const handler = () => { const t = setTimeout(updatePreview, 350); return () => clearTimeout(t); };
    editor.on("update", handler);
    return () => editor.off("update", handler);
  }, [editor, updatePreview]);

  const insertVariable = (key) => editor?.chain().focus().insertContent(key).run();

  const loadTemplate = (t) => {
    if (!editor) return;
    if (editor.getText() && !window.confirm("Skifter du skabelon, mistes den nuværende tekst. Fortsæt?")) return;
    setSubject(t.subject);
    editor.commands.setContent(t.content_html || "");
    toast.success(`Skabelon "${t.name}" indlæst`);
  };

  const saveAsTemplate = async () => {
    if (!editor) return;
    const name = window.prompt("Navngiv skabelonen", subject || "Ny skabelon");
    if (!name) return;
    try {
      await api.post("/admin/mail/templates", { name, subject, content_html: editor.getHTML() });
      qc.invalidateQueries({ queryKey: ["mail-templates"] });
      toast.success(`Skabelon "${name}" gemt`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke gemme skabelon"); }
  };

  const deleteTemplate = async (t) => {
    if (t.is_builtin) return;
    if (!window.confirm(`Slet skabelonen "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/mail/templates/${t.template_id}`);
      qc.invalidateQueries({ queryKey: ["mail-templates"] });
      toast.success("Skabelon slettet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke slette"); }
  };

  const sendTest = async () => {
    if (!editor) return;
    if (!subject) return toast.error("Emne er påkrævet");
    setSending(true);
    try {
      const r = await api.post("/admin/mail/send", { subject, content_html: editor.getHTML(), to_all: false, test_only: true, campaign_name: subject });
      toast.success(`Testmail sendt (${r.data.sent}/${r.data.recipients})`);
      qc.invalidateQueries({ queryKey: ["mail-campaigns"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl ved afsendelse"); }
    finally { setSending(false); }
  };

  const sendAll = async () => {
    if (!editor) return;
    setConfirmOpen(false);
    setSending(true);
    try {
      const r = await api.post("/admin/mail/send", { subject, content_html: editor.getHTML(), to_all: true, test_only: false, campaign_name: subject });
      toast.success(`Sendt til ${r.data.sent} af ${r.data.recipients} spillere`);
      if (r.data.errors) toast.error(`${r.data.errors} mails fejlede`);
      qc.invalidateQueries({ queryKey: ["mail-campaigns"] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl ved afsendelse"); }
    finally { setSending(false); }
  };

  const refreshCampaign = async (id) => {
    try {
      await api.post(`/admin/mail/campaigns/${id}/refresh`);
      qc.invalidateQueries({ queryKey: ["mail-campaigns"] });
      toast.success("Statistik opdateret");
    } catch { toast.error("Kunne ikke hente statistik"); }
  };

  return (
    <div className="space-y-8" data-testid="mail-center">
      {/* Provider status */}
      <div className="card-flat p-4 flex items-center gap-3 flex-wrap">
        <Mail size={16} className="text-zinc-400" />
        {status?.configured ? (
          <div className="text-sm">
            <span className="text-green-400 font-semibold">Aktiv</span>
            <span className="text-zinc-500"> · Afsender: </span>
            <span className="text-zinc-300">{status.sender_name} &lt;{status.sender}&gt;</span>
          </div>
        ) : (
          <div className="text-sm text-red-400 flex items-center gap-2"><AlertTriangle size={14}/> Mail-provider ikke konfigureret</div>
        )}
        <div className="ml-auto text-sm text-zinc-500" data-testid="mail-audience-count">
          Modtagerliste: <span className="text-white font-semibold">{audience?.total ?? "–"}</span> spillere
        </div>
      </div>

      {/* Templates row */}
      <div className="card-flat p-5" data-testid="mail-templates">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-zinc-400" />
            <h3 className="font-display font-bold text-lg">Skabeloner</h3>
          </div>
          <button onClick={saveAsTemplate} className="text-sm text-zinc-300 hover:text-white flex items-center gap-1" data-testid="mail-save-template-btn">
            <Save size={12} /> Gem nuværende som skabelon
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {templates?.map(t => (
            <div key={t.template_id} className="flex items-center border border-white/10 rounded-full overflow-hidden">
              <button onClick={() => loadTemplate(t)} data-testid={`mail-tpl-${t.template_id}`}
                className="px-4 py-1.5 text-sm text-zinc-200 hover:bg-white/5 flex items-center gap-2">
                {t.is_builtin && <span className="text-xs text-orange-400">•</span>}
                {t.name}
              </button>
              {!t.is_builtin && (
                <button onClick={() => deleteTemplate(t)} className="px-2 py-1.5 text-zinc-500 hover:text-red-400 border-l border-white/10" data-testid={`mail-tpl-delete-${t.template_id}`}>
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
          {(!templates || templates.length === 0) && (
            <div className="text-sm text-zinc-500">Ingen skabeloner endnu.</div>
          )}
        </div>
      </div>

      {/* Composer + preview */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-flat p-6 space-y-4">
          <h3 className="font-display font-bold text-xl">Skriv mail</h3>

          <div>
            <Label>Emne</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Nye festivalnavne er landet" data-testid="mail-subject-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Label>Indhold</Label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500">Indsæt variabel:</span>
                {VARIABLES.map(v => (
                  <button
                    key={v.key}
                    onClick={() => insertVariable(v.key)}
                    className="text-xs px-2 py-1 rounded border border-white/10 text-zinc-300 hover:bg-white/5 font-mono"
                    data-testid={`mail-var-${v.key.replace(/[{}]/g, "")}`}
                    title={`Erstattes af: ${v.example}`}
                  >{v.key}</button>
                ))}
              </div>
            </div>
            <div className="border border-white/10 rounded-md bg-black/40 overflow-hidden">
              <Toolbar editor={editor} />
              <div className="border-t border-white/10 mail-editor-container">
                <EditorContent editor={editor} />
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2">Rich text-editor · ingen HTML nødvendig. Brug knap-ikonet for at indsætte "SE DIN SCORE"-knapper.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={sendTest} disabled={sending} variant="outline" className="rounded-full" data-testid="mail-send-test-btn">
              <Send size={14} className="mr-2"/> Send testmail til mig
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={sending || !status?.configured}
              className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold"
              data-testid="mail-send-all-btn"
            >
              <Send size={14} className="mr-2"/> Send til alle
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="card-flat p-6">
          <div className="flex items-center gap-2 mb-4">
            <Eye size={14} className="text-zinc-400" />
            <h3 className="font-display font-bold text-xl">Forhåndsvisning</h3>
          </div>
          <div className="border border-white/10 rounded-lg overflow-hidden bg-white" data-testid="mail-preview-frame">
            <div className="bg-zinc-100 px-4 py-2 border-b border-zinc-200">
              <div className="text-xs text-zinc-500">Emne</div>
              <div className="text-sm text-zinc-900 font-semibold">{preview?.subject || subject}</div>
            </div>
            <iframe title="mail-preview" srcDoc={preview?.html || ""} className="w-full h-[540px] bg-white" />
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="mail-confirm-modal">
          <div className="card-flat p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="text-orange-400" />
              <h3 className="font-display font-bold text-2xl">Bekræft afsendelse</h3>
            </div>
            <p className="text-zinc-300 mb-6">
              Du er ved at sende denne mail til <span className="font-bold text-white">{audience?.total ?? "?"}</span> aktive spillere. Er du sikker?
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" className="rounded-full" onClick={() => setConfirmOpen(false)} data-testid="mail-confirm-cancel">Annuller</Button>
              <Button className="rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold" onClick={sendAll} data-testid="mail-confirm-send">Send mail</Button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div>
        <h3 className="font-display font-bold text-2xl mb-4">Historik</h3>
        <div className="card-flat overflow-hidden">
          {(!campaigns || campaigns.length === 0) && (
            <div className="p-6 text-zinc-500 text-center">Ingen mails sendt endnu.</div>
          )}
          {campaigns?.map((c, i) => (
            <div key={c.campaign_id} className={`p-5 ${i < campaigns.length - 1 ? "border-b border-white/5" : ""}`} data-testid={`mail-history-row-${i}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs text-zinc-500">{new Date(c.created_at).toLocaleDateString("da-DK", { day: "numeric", month: "long", year: "numeric" })}{c.test_only ? " · TEST" : ""}</div>
                  <div className="font-semibold mt-0.5">{c.subject}</div>
                </div>
                <button onClick={() => refreshCampaign(c.campaign_id)} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid={`mail-refresh-${c.campaign_id}`}>
                  <RefreshCw size={12}/> Hent statistik
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                <Stat label="Modtagere" value={c.recipient_count}/>
                <Stat label="Leveret" value={c.delivered_count ?? "–"}/>
                <Stat label="Åbninger" value={c.opened_count ?? "–"} sub={c.open_rate ? `${c.open_rate}%` : null} icon={<Eye size={12}/>}/>
                <Stat label="Klik" value={c.clicked_count ?? "–"} sub={c.click_rate ? `${c.click_rate}%` : null} icon={<MousePointerClick size={12}/>}/>
                <Stat label="Fejl" value={c.error_count || 0}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon }) {
  return (
    <div>
      <div className="label-eyebrow flex items-center gap-1">{icon}{label}</div>
      <div className="font-score text-2xl leading-tight mt-1">
        {value}
        {sub && <span className="text-zinc-500 text-sm ml-2 font-body">{sub}</span>}
      </div>
    </div>
  );
}
